import React, { useEffect, useState, useCallback } from 'react';
import { useSimulationStore } from './store/useSimulationStore';
import { useCanvasStore } from './store/useCanvasStore';
import { AppHeader } from './components/layout/AppHeader';
import { NavigationTabs, MainTabType } from './components/layout/NavigationTabs';
import { ResponsiveContainer } from './components/layout/ResponsiveContainer';
import { NodeEditorDrawer } from './components/modals/NodeEditorDrawer';
import { PresetSelectorModal } from './components/modals/PresetSelectorModal';
import { StructureLibraryModal } from './components/modals/StructureLibraryModal';
import { DocumentExportModal } from './components/modals/DocumentExportModal';
import { WebVitalsModal } from './components/modals/WebVitalsModal';
import { AuthGatewayModal } from './components/modals/AuthGatewayModal';
import { AdminAccountsModal } from './components/modals/AdminAccountsModal';
import { OnboardingTour, ONBOARDING_STORAGE_KEY } from './components/modals/OnboardingTour';
import { WebVitalsState } from './types/vitals';
import { getWebVitalsState, initWebVitals, subscribeToWebVitals } from './services/vitals';
import { checkStoredAuthSession, clearAuthSession, getStoredUser, verifyServerSession, AuthUser } from './services/auth';
import { clearSavedStructuresCache } from './services/structureLibrary';
import { findNodeById } from './utils/treeUtils';
import { ErrorBoundary } from './components/common/ErrorBoundary';

export const App: React.FC = () => {
  // Authentication Gateway State & Current User Profile
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => checkStoredAuthSession());
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => getStoredUser());

  // Navigation & Modal states
  const [activeMobileTab, setActiveMobileTab] = useState<MainTabType>('map');
  const [isNodeEditorOpen, setIsNodeEditorOpen] = useState(false);
  const [isPresetModalOpen, setIsPresetModalOpen] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isDocumentExportOpen, setIsDocumentExportOpen] = useState(false);
  const [isWebVitalsModalOpen, setIsWebVitalsModalOpen] = useState(false);
  const [isAdminAccountsOpen, setIsAdminAccountsOpen] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [startTourDirectly, setStartTourDirectly] = useState(false);
  const [vitalsState, setVitalsState] = useState<WebVitalsState>(getWebVitalsState());

  // Store bindings
  const mode = useSimulationStore((state) => state.mode);
  const selectedMonth = useSimulationStore((state) => state.selectedMonth);
  const activePreset = useSimulationStore((state) => state.activePreset);
  const sandboxTree = useSimulationStore((state) => state.sandboxTree);
  const loadPreset = useSimulationStore((state) => state.loadPreset);
  const loadCustomTree = useSimulationStore((state) => state.loadCustomTree);
  const transferSandboxToPathway = useSimulationStore((state) => state.transferSandboxToPathway);
  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);
  const getCurrentPayoutBreakdown = useSimulationStore((state) => state.getCurrentPayoutBreakdown);
  const updateSandboxNode = useSimulationStore((state) => state.updateSandboxNode);
  const deleteSandboxNode = useSimulationStore((state) => state.deleteSandboxNode);
  const toggleSandboxNodeActive = useSimulationStore((state) => state.toggleSandboxNodeActive);
  const setMode = useSimulationStore((state) => state.setMode);
  const setSelectedMonth = useSimulationStore((state) => state.setSelectedMonth);
  const setCommissionRateCoefficient = useSimulationStore((state) => state.setCommissionRateCoefficient);
  const addSandboxNode = useSimulationStore((state) => state.addSandboxNode);

  const selectedNodeId = useCanvasStore((state) => state.selectedNodeId);
  const setSelectedNodeId = useCanvasStore((state) => state.setSelectedNodeId);

  // Initialize Web Vitals Telemetry
  useEffect(() => {
    initWebVitals();
    const unsubscribe = subscribeToWebVitals((state) => {
      setVitalsState(state);
    });
    return unsubscribe;
  }, []);

  // Verify server session on load (in case account was blocked or revoked in Cloudflare KV)
  useEffect(() => {
    if (checkStoredAuthSession()) {
      verifyServerSession().then((isValid) => {
        if (!isValid) {
          handleLockSession();
        } else {
          setCurrentUser(getStoredUser());
        }
      });
    }
  }, []);

  // First-time interactive onboarding tour check
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (!completed) {
          const timer = setTimeout(() => {
            setStartTourDirectly(false);
            setIsTourOpen(true);
          }, 800);
          return () => clearTimeout(timer);
        }
      } catch {
        // Ignore
      }
    }
  }, [isAuthenticated]);

  const rootNode = mode === 'sandbox' ? sandboxTree : getCurrentRootNode();
  const payout = getCurrentPayoutBreakdown();
  const selectedNode = selectedNodeId ? findNodeById(rootNode, selectedNodeId) : null;

  const handleOpenNodeEditor = useCallback((nodeId: string) => {
    setSelectedNodeId(nodeId);
    setIsNodeEditorOpen(true);
  }, []);

  const handleCloseNodeEditor = useCallback(() => {
    setIsNodeEditorOpen(false);
  }, []);

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
    setCurrentUser(getStoredUser());
  };

  const handleLockSession = () => {
    clearAuthSession();
    clearSavedStructuresCache();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setIsAdminAccountsOpen(false);
    setIsLibraryOpen(false);
    setIsPresetModalOpen(false);
    setIsNodeEditorOpen(false);
    setIsTourOpen(false);
  };

  const handleStartTour = useCallback(() => {
    setMode('pathway');
    setSelectedMonth(1);
    setCommissionRateCoefficient(1.0);
    setIsNodeEditorOpen(false);
    setStartTourDirectly(true);
    setIsTourOpen(true);
  }, [setMode, setSelectedMonth, setCommissionRateCoefficient]);

  const handleSimulateLibraryStructure = (tree: any) => {
    loadCustomTree(tree);
    transferSandboxToPathway();
  };

  return (
    <div className="h-[100dvh] max-h-[100dvh] bg-[#0A0F1D] text-text-primary flex flex-col font-sans selection:bg-accent-emerald/30 selection:text-white overflow-hidden">
      {/* App Header */}
      <AppHeader
        vitalsState={vitalsState}
        onOpenWebVitals={() => setIsWebVitalsModalOpen(true)}
        onOpenPresets={() => setIsPresetModalOpen(true)}
        onOpenLibrary={() => setIsLibraryOpen(true)}
        onOpenDocumentExport={() => setIsDocumentExportOpen(true)}
        onLockSession={handleLockSession}
        currentUser={currentUser}
        onOpenAdminAccounts={() => setIsAdminAccountsOpen(true)}
        onStartTour={handleStartTour}
      />

      {/* Main Responsive Workbench */}
      <ResponsiveContainer
        activeMobileTab={activeMobileTab}
        onOpenNodeEditor={handleOpenNodeEditor}
        onOpenPresets={() => setIsPresetModalOpen(true)}
        onOpenLibrary={() => setIsLibraryOpen(true)}
        onOpenDocumentExport={() => setIsDocumentExportOpen(true)}
      />

      {/* Mobile Bottom Navigation Bar */}
      <NavigationTabs
        activeTab={activeMobileTab}
        onChange={setActiveMobileTab}
      />

      {/* Authentication Gateway Modal */}
      <AuthGatewayModal
        isOpen={!isAuthenticated}
        onAuthenticated={handleAuthenticated}
      />

      {/* Selected Node Editor Drawer */}
      {selectedNode && (
        <NodeEditorDrawer
          isOpen={isAuthenticated && isNodeEditorOpen}
          onClose={() => setIsNodeEditorOpen(false)}
          selectedNode={selectedNode}
          onUpdateNode={updateSandboxNode}
          onAddChild={(parentId) => addSandboxNode(parentId)}
          onDeleteNode={deleteSandboxNode}
          onToggleActive={toggleSandboxNodeActive}
          isReadOnly={mode === 'pathway'}
        />
      )}

      {/* Starter Team Preset Selector Modal */}
      <ErrorBoundary
        name="PresetSelectorModal"
        onError={() => setIsPresetModalOpen(false)}
      >
        <PresetSelectorModal
          isOpen={isAuthenticated && isPresetModalOpen}
          onClose={() => setIsPresetModalOpen(false)}
          activePresetId={activePreset}
          onSelectPreset={loadPreset}
        />
      </ErrorBoundary>

      {/* Consultant Structure Library Modal (Cloudflare KV Synchronized) */}
      <ErrorBoundary
        name="StructureLibraryModal"
        onError={() => setIsLibraryOpen(false)}
      >
        <StructureLibraryModal
          isOpen={isAuthenticated && isLibraryOpen}
          onClose={() => setIsLibraryOpen(false)}
          currentTree={rootNode}
          onLoadStructure={loadCustomTree}
          onSimulateStructure={handleSimulateLibraryStructure}
          currentUser={currentUser}
        />
      </ErrorBoundary>

      {/* Admin Consultant Management Modal */}
      <ErrorBoundary
        name="AdminAccountsModal"
        onError={() => setIsAdminAccountsOpen(false)}
      >
        <AdminAccountsModal
          isOpen={isAuthenticated && isAdminAccountsOpen && currentUser?.role === 'admin'}
          onClose={() => setIsAdminAccountsOpen(false)}
        />
      </ErrorBoundary>

      {/* Comprehensive Document & PDF Export Modal */}
      <ErrorBoundary
        name="DocumentExportModal"
        onError={() => setIsDocumentExportOpen(false)}
      >
        <DocumentExportModal
          isOpen={isAuthenticated && isDocumentExportOpen}
          onClose={() => setIsDocumentExportOpen(false)}
          rootNode={rootNode}
          payout={payout}
          simulationMonth={mode === 'pathway' ? selectedMonth : undefined}
        />
      </ErrorBoundary>

      {/* Google Web-Vitals Diagnostics Modal - Admin only */}
      <ErrorBoundary
        name="WebVitalsModal"
        onError={() => setIsWebVitalsModalOpen(false)}
      >
        <WebVitalsModal
          isOpen={isAuthenticated && isWebVitalsModalOpen && currentUser?.role === 'admin'}
          onClose={() => setIsWebVitalsModalOpen(false)}
          vitalsState={vitalsState}
        />
      </ErrorBoundary>

      {/* Interactive Onboarding Spotlight Tour guarded by ErrorBoundary */}
      <ErrorBoundary
        name="OnboardingTour"
        onError={(err) => {
          console.error('[App] OnboardingTour encountered error, safely closing tour:', err);
          setIsTourOpen(false);
          setStartTourDirectly(false);
        }}
      >
        <OnboardingTour
          isOpen={isAuthenticated && isTourOpen}
          onClose={() => {
            setIsTourOpen(false);
            setStartTourDirectly(false);
          }}
          startDirectly={startTourDirectly}
          isNodeEditorOpen={isNodeEditorOpen}
          onOpenNodeEditor={handleOpenNodeEditor}
          onCloseNodeEditor={handleCloseNodeEditor}
          onNavigateTab={setActiveMobileTab}
        />
      </ErrorBoundary>
    </div>
  );
};

export default App;
